import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, CardTitle, Button } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const Dashboard = () => {
  const navigate = useNavigate();
  const [ssps, setSSPs] = useState([]);
  const [controls, setControls] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

      const [sspResponse, controlsResponse, evidenceResponse] = await Promise.all([
        axios.get(`${baseURL}/ssp-studio/ssps`, { withCredentials: true }),
        axios.get(`${baseURL}/ssp-studio/controls`, { withCredentials: true }),
        axios.get(`${baseURL}/ssp-studio/evidence`, { withCredentials: true })
      ]);

      setSSPs(sspResponse.data.data || []);
      setControls(controlsResponse.data.data || []);
      setEvidence(evidenceResponse.data.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching SSP Studio data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center mt-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="alert alert-danger mt-3">
            <h5>Error loading SSP Studio</h5>
            <p>{error}</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg={12}>
            <div className="d-flex align-items-center justify-content-between mb-4">
              <h4 className="mb-0">SSP Studio Dashboard</h4>
              <Button color="primary" onClick={() => navigate('/ssp-studio/create')}>
                <i className="mdi mdi-plus me-1"></i>
                Create New SSP
              </Button>
            </div>
          </Col>
        </Row>

        <Row>
          <Col lg={4}>
            <Card>
              <CardBody>
                <CardTitle className="mb-4">
                  <i className="mdi mdi-file-document-multiple text-primary me-2"></i>
                  System Security Plans
                </CardTitle>
                <div className="text-center">
                  <h2 className="mb-0">{ssps.length}</h2>
                  <p className="text-muted">Total SSPs</p>
                </div>
                {ssps.length > 0 ? (
                  <div className="mt-3">
                    <h6>Recent SSPs:</h6>
                    {ssps.slice(0, 5).map((ssp) => (
                      <div key={ssp._id} className="border-bottom py-2">
                        <div className="d-flex justify-content-between">
                          <span
                            className="cursor-pointer text-primary"
                            onClick={() => navigate(`/ssp-studio/ssp/${ssp._id}`)}
                            style={{ cursor: 'pointer' }}
                          >
                            {ssp.name}
                          </span>
                          <span className={`badge badge-soft-${
                            ssp.status === 'approved' ? 'success' :
                            ssp.status === 'in-review' ? 'warning' : 'info'
                          }`}>
                            {ssp.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-3">
                    <p className="text-muted">No SSPs created yet</p>
                    <Button size="sm" color="primary" outline onClick={() => navigate('/ssp-studio/create')}>
                      Create Your First SSP
                    </Button>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          <Col lg={4}>
            <Card>
              <CardBody>
                <CardTitle className="mb-4">
                  <i className="mdi mdi-shield-check text-success me-2"></i>
                  Security Controls
                </CardTitle>
                <div className="text-center">
                  <h2 className="mb-0">{controls.length}</h2>
                  <p className="text-muted">Total Controls</p>
                </div>
                {controls.length > 0 && (
                  <div className="mt-3">
                    <Row>
                      <Col xs={6}>
                        <div className="text-center">
                          <h5 className="text-success">
                            {controls.filter(c => c.implementationStatus === 'implemented').length}
                          </h5>
                          <p className="text-muted mb-0">Implemented</p>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-center">
                          <h5 className="text-warning">
                            {controls.filter(c => c.implementationStatus === 'partial').length}
                          </h5>
                          <p className="text-muted mb-0">Partial</p>
                        </div>
                      </Col>
                    </Row>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          <Col lg={4}>
            <Card>
              <CardBody>
                <CardTitle className="mb-4">
                  <i className="mdi mdi-file-document text-info me-2"></i>
                  Evidence
                </CardTitle>
                <div className="text-center">
                  <h2 className="mb-0">{evidence.length}</h2>
                  <p className="text-muted">Total Evidence Items</p>
                </div>
                {evidence.length > 0 && (
                  <div className="mt-3">
                    <h6>Evidence Types:</h6>
                    {['scan', 'policy', 'procedure', 'config'].map(type => {
                      const count = evidence.filter(e => e.type === type).length;
                      return count > 0 ? (
                        <div key={type} className="d-flex justify-content-between py-1">
                          <span className="text-capitalize">{type}</span>
                          <span className="badge bg-light text-dark">{count}</span>
                        </div>
                      ) : null;
                    })}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-4">Quick Actions</CardTitle>
                <Row>
                  <Col md={3}>
                    <div
                      className="text-center p-3 border rounded cursor-pointer"
                      onClick={() => navigate('/ssp-studio/templates')}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className="mdi mdi-file-document-multiple font-size-24 text-primary mb-2"></i>
                      <h6>Manage Templates</h6>
                      <p className="text-muted small mb-0">Create and manage SSP templates</p>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div
                      className="text-center p-3 border rounded cursor-pointer"
                      onClick={() => navigate('/ssp-studio/create')}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className="mdi mdi-plus-circle font-size-24 text-success mb-2"></i>
                      <h6>Create SSP</h6>
                      <p className="text-muted small mb-0">Start a new security plan</p>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div
                      className="text-center p-3 border rounded cursor-pointer"
                      onClick={() => navigate('/ssp-studio/ssps')}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className="mdi mdi-file-document font-size-24 text-warning mb-2"></i>
                      <h6>Browse SSPs</h6>
                      <p className="text-muted small mb-0">View and edit existing SSPs</p>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div
                      className="text-center p-3 border rounded cursor-pointer"
                      onClick={() => navigate('/ssp-studio/evidence')}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className="mdi mdi-upload font-size-24 text-info mb-2"></i>
                      <h6>Upload Evidence</h6>
                      <p className="text-muted small mb-0">Attach supporting documentation</p>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Dashboard;
